import React from 'react';
import {
	useHealthPlanUserIdQuery,
	useLazyHealthPlanRequirementsQuery,
} from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import { HealthPlanCode } from '../../../../Enums/HealthPlanCode.enum';
import RequirementsListItem from '../../../Requirements/Components/RequirementsListItem';
import useApiClient from '../../../Base/Hooks/useApiClient';
import apiClient from '../../../../Services/ApiClient';
import useNotification from '../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../Hooks/useTranslation';
import MissingHealthPlanUserPlaceholder from '../MissingHealthPlanUserPlaceholder/MissingHealthPlanUserPlaceholder';
import ScrollContainer from '../../../Base/Components/ScrollContainer';
import FlatList from '../../../Base/Components/FlatList';
import useHealthPlanRequirements from '../../Hooks/useHealthPlanRequirements';
import useFlatData from '../../../Base/Hooks/useFlatData';
import Legend from '../../../Base/Components/Legend/Legend';
import { LegendItemScheme } from '../../../Base/Components/Legend/LegendItem';
// @ts-ignore
import styles from './HealthPlanRequirements.module.scss';

interface Props {
	patientId: string;
	healthPlanCode: HealthPlanCode;
	setIsLoading: (isLoading: boolean) => void;
	workplaceId: string;
}

interface HealthPlanRequirementsProps {
	healthPlanUserId: string;
	healthPlanId: string;
	setIsLoading: (isLoading: boolean) => void;
}

const WRAPPER_ID = 'healthPlanRequirementsWrapper';

const legendItems: LegendItemScheme[] = [
	{
		title: 'component.healthPlanRequirementsWrapper.legend.waitingForUpload',
		icon: '/dist/requirements/circle-xmark-solid.svg',
	},
	{
		title: 'component.healthPlanRequirementsWrapper.legend.waitingForApprove',
		icon: '/dist/requirements/circle-check-solid-gray.svg',
	},
	{
		title: 'component.healthPlanRequirementsWrapper.legend.approved',
		icon: '/dist/requirements/circle-check-solid.svg',
	},
];

const HealthPlanRequirementsWrapper: React.FC<Props> = ({
	patientId,
	healthPlanCode,
	setIsLoading,
	workplaceId,
}) => {
	const { data, isLoading } = useHealthPlanUserIdQuery({
		patientId,
		healthPlanCode,
		workplaceId,
	});

	if (isLoading) {
		return <Loading />;
	}

	if (!data?.healthPlanUser?.id || !data.healthPlanUser.healthPlanId) {
		return <MissingHealthPlanUserPlaceholder />;
	}

	return (
		<div className={styles.wrapper}>
			<Legend items={legendItems} />
			<HealthPlanRequirements
				healthPlanUserId={data.healthPlanUser.id}
				healthPlanId={data.healthPlanUser.healthPlanId}
				setIsLoading={setIsLoading}
			/>
		</div>
	);
};

const HealthPlanRequirements: React.FC<HealthPlanRequirementsProps> = ({
	healthPlanUserId,
	healthPlanId,
	setIsLoading,
}) => {
	const { displayError } = useApiClient();
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();

	const [getHealthPlanRequirementsQuery, { data, isLoading }] =
		useLazyHealthPlanRequirementsQuery();
	const { getHealthPlanRequirements, getNextPage, page, setPage } =
		useHealthPlanRequirements(getHealthPlanRequirementsQuery);

	const { flatData, hasMoreData, loadData, setFilter } = useFlatData(
		getHealthPlanRequirements,
		{
			data: data?.healthPlanUserItems?.data,
			totalPages: data?.healthPlanUserItems?.meta?.totalPages,
			page: data?.healthPlanUserItems?.meta?.page,
		},
		page,
		setPage,
		{
			healthPlanUserId: healthPlanUserId,
		},
	);

	const handleUpdateItem = async (requirementItemId: string, data: object) => {
		setIsLoading(true);
		try {
			const response = await apiClient.patch(
				`/health-plan/${healthPlanId}/user/${healthPlanUserId}/item/${requirementItemId}`,
				data,
			);
			if (response.status === 200) {
				showNotificationSuccess({
					content: t('base.saved'),
				});
				loadData();
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	const handleUpdateNote = async (requirementItemId: string, note: string) => {
		await handleUpdateItem(requirementItemId, {
			extra: {
				note: note,
			},
		});
	};

	const handleApprove = async (requirementItemId: string) => {
		await handleUpdateItem(requirementItemId, {
			extra: {
				approved: true,
			},
		});
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<ScrollContainer wrapperId={WRAPPER_ID}>
			<FlatList
				onRefresh={loadData}
				onLoadMore={getNextPage}
				data={flatData}
				isLoading={isLoading}
				hasMoreData={hasMoreData}
				scrollableTargetId={WRAPPER_ID}
				itemKey={'id'}
				renderItem={item => (
					<RequirementsListItem
						item={item}
						onApprove={handleApprove}
						onNoteEdit={handleUpdateNote}
					/>
				)}
			/>
		</ScrollContainer>
	);
};

export default HealthPlanRequirementsWrapper;
